/**
 * Signup Page
 * 
 * User registration page with email/password and social signup.
 * 
 * @module pages/auth/SignupPage
 */

import { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { httpsCallable } from 'firebase/functions';
import { useAuth } from '@/contexts/AuthContext';
import { usePageTitle } from '@/hooks';
import { Button, Input, Alert } from '@/components/ui';
import { SocialAuthButtons } from '@/components/auth';
import { links } from '@/lib/config';
import { auth, functions } from '@/lib/firebase';
import { ROLE_LABELS, type OrgRole } from '@/lib/firebase-types';
import { Eye, EyeOff, Check, Mail } from 'lucide-react';

// =============================================================================
// Password Requirements
// =============================================================================

function PasswordRequirements({ password }: { password: string }) {
  const requirements = [
    { label: 'At least 8 characters', met: password.length >= 8 },
    { label: 'Has a number', met: /\d/.test(password) },
    { label: 'Has a letter', met: /[a-zA-Z]/.test(password) },
  ];

  if (!password) return null;

  return (
    <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1">
      {requirements.map((req) => (
        <div
          key={req.label}
          className={`flex items-center gap-1.5 text-xs ${
            req.met ? 'text-success-600' : 'text-gray-400'
          }`}
        >
          <Check className={`h-3.5 w-3.5 ${req.met ? 'opacity-100' : 'opacity-0'}`} />
          {req.label}
        </div>
      ))}
    </div>
  );
}

// =============================================================================
// Component
// =============================================================================

// Type for location state
interface LocationState {
  from?: { pathname?: string; search?: string };
  isInvite?: boolean;
  inviteDetails?: {
    email?: string;
    organizationName?: string;
    inviterName?: string;
    role?: string;
  };
}

export function SignupPage() {
  usePageTitle('Sign Up');

  const navigate = useNavigate();
  const location = useLocation();
  const { signUp, error, clearError } = useAuth();

  // Get invite details from location state
  const locationState = location.state as LocationState | null;
  const fromState = locationState?.from;
  const from = fromState ? `${fromState.pathname || '/overview'}${fromState.search || ''}` : '/overview';
  const isInvite = locationState?.isInvite === true;
  const inviteDetails = locationState?.inviteDetails;

  const [email, setEmail] = useState(inviteDetails?.email || '');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);

  // Clear any existing auth errors on mount
  useEffect(() => {
    clearError();
  }, [clearError]);

  const isPasswordValid =
    password.length >= 8 && /\d/.test(password) && /[a-zA-Z]/.test(password);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!isPasswordValid) return;

    setLoading(true);

    try {
      await signUp(email, password);

      // Send email verification via Cloud Function (uses Resend for better deliverability)
      // We catch errors silently here - the user can resend from the verify-email page
      // Skip verification email for invited users - they're auto-verified when they accept
      const currentUser = auth.currentUser;
      if (currentUser && !currentUser.emailVerified && !isInvite) {
        try {
          const sendVerificationEmail = httpsCallable<void, { success: boolean }>(
            functions,
            'sendVerificationEmail'
          );
          await sendVerificationEmail();
        } catch (emailError) {
          // Log but don't block - user can resend on verify-email page
          console.error('Failed to send verification email:', emailError);
        }
      }

      // Redirect to verify email page (unless accepting an invite)
      if (isInvite) {
        navigate(from, { replace: true });
      } else {
        navigate('/verify-email', { replace: true });
      }
    } catch {
      // Error handled in AuthContext
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex overflow-auto no-scrollbar">
      {/* Left side - Form */}
      <div className="flex-1 flex flex-col justify-center px-4 sm:px-6 lg:px-8 py-12">
        <div className="w-full max-w-sm mx-auto">
          {/* Logo */}
          <div className="flex items-center gap-2 mb-8">
            <div className="flex items-center justify-center h-10 w-10 rounded-xl bg-brand-500 overflow-hidden">
              <svg className="w-10 h-10" viewBox="0 0 32 32" fill="none">
                <path d="M7 10L13 16L7 22" stroke="white" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" opacity="0.5"/>
                <path d="M13 10L19 16L13 22" stroke="white" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" opacity="0.75"/>
                <path d="M19 10L25 16L19 22" stroke="white" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </div>
            <span className="font-display font-semibold text-xl text-gray-900">
              Hummingbird
            </span>
          </div>

          {/* Header */}
          <div className="mb-8">
            <h1 className="text-2xl font-display font-semibold text-gray-900">
              {isInvite ? 'Accept your invitation' : 'Create your free account'}
            </h1>
            <p className="text-gray-500 mt-1">
              {isInvite
                ? 'Create an account to join the team.'
                : 'Get started today in just a few clicks.'}
            </p>
          </div>

          {/* Invite Banner */}
          {isInvite && (
            <div className="mb-6 p-4 bg-brand-50 border border-brand-200 rounded-lg flex items-start gap-3">
              <Mail className="h-5 w-5 text-brand-600 mt-0.5 flex-shrink-0" />
              <div className="text-sm">
                <p className="font-medium text-brand-900">You've been invited!</p>
                <p className="text-brand-700 mt-0.5">
                  {inviteDetails?.inviterName && inviteDetails?.organizationName ? (
                    <>
                      <span className="font-medium">{inviteDetails.inviterName}</span> has invited you to join{' '}
                      <span className="font-medium">{inviteDetails.organizationName}</span>
                      {inviteDetails.role && <> as {inviteDetails.role === 'admin' || inviteDetails.role === 'org_admin' ? 'an' : 'a'} <span className="font-medium">{ROLE_LABELS[inviteDetails.role as OrgRole] || inviteDetails.role}</span></>}.
                    </>
                  ) : (
                    'Sign up to accept your team invitation.'
                  )}
                </p>
              </div>
            </div>
          )}

          {/* Error Alert */}
          {error && (
            <Alert variant="error" className="mb-6" onDismiss={clearError}>
              {error}
            </Alert>
          )}

          {/* Social Auth */}
          <SocialAuthButtons mode="signup" />

          {/* Divider */}
          <div className="relative my-6">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-gray-200" />
            </div>
            <div className="relative flex justify-center text-sm">
              <span className="px-2 bg-white text-gray-500">or</span>
            </div>
          </div>

          {/* Email Form */}
          <form onSubmit={handleSubmit} className="space-y-4">
            <Input
              label="Email"
              type="email"
              name="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@example.com"
              autoComplete="email"
              autoFocus
              required
            />

            <div>
              <Input
                label="Password"
                id="password"
                type={showPassword ? 'text' : 'password'}
                name="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                autoComplete="new-password"
                required
                rightElement={
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    {showPassword ? (
                      <EyeOff className="h-5 w-5" />
                    ) : (
                      <Eye className="h-5 w-5" />
                    )}
                  </button>
                }
              />
              <PasswordRequirements password={password} />
            </div>

            <Button
              type="submit"
              fullWidth
              loading={loading}
              disabled={!isPasswordValid}
            >
              Create account
            </Button>
          </form>

          {/* Terms */}
          <p className="text-center text-xs text-gray-500 mt-6">
            By creating an account, you agree to our{' '}
            <a href={links.terms} className="text-brand-600 hover:text-brand-700">
              Terms of Service
            </a>
            ,{' '}
            <a href={links.privacy} className="text-brand-600 hover:text-brand-700">
              Privacy Policy
            </a>
            , and{' '}
            <a href={links.acceptableUse} className="text-brand-600 hover:text-brand-700">
              Acceptable Use Policy
            </a>.
          </p>

          {/* Sign in link */}
          <p className="text-center text-sm text-gray-500 mt-6">
            Already have an account?{' '}
            <Link
              to="/login"
              state={location.state}
              className="font-medium text-brand-600 hover:text-brand-700"
            >
              Sign in
            </Link>
          </p>
        </div>
      </div>

      {/* Right side - Decorative */}
      <div className="hidden lg:flex lg:flex-1 bg-gradient-to-br from-brand-500 to-brand-600 relative overflow-hidden">
        {/* Background pattern */}
        <div className="absolute inset-0 opacity-10">
          <svg className="w-full h-full" xmlns="http://www.w3.org/2000/svg">
            <defs>
              <pattern id="grid" width="40" height="40" patternUnits="userSpaceOnUse">
                <path d="M 40 0 L 0 0 0 40" fill="none" stroke="white" strokeWidth="1"/>
              </pattern>
            </defs>
            <rect width="100%" height="100%" fill="url(#grid)" />
          </svg>
        </div>

        {/* Content */}
        <div className="relative flex flex-col justify-center px-12 text-white">
          <h2 className="text-4xl font-display font-semibold mb-4">
          Engineered to an unreasonable standard.
          </h2>
          <p className="text-xl text-white/80 max-w-md">
          We spent years shaving off milliseconds that other platforms ignore. You get the benefit.
          </p>

          {/* Features */}
          <div className="mt-12 space-y-4">
            {[
              '<50ms median response times',
              '300+ edge locations',
              '99.99% uptime SLA',
              'Set up in 5 minutes or less',
              '2,000 forever free requests',
              'No credit card required'
            ].map((feature) => (
              <div key={feature} className="flex items-center gap-3">
                <div className="flex items-center justify-center h-6 w-6 rounded-full bg-white/20">
                  <Check className="h-4 w-4" />
                </div>
                <span>{feature}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
